import { useEffect, useState } from 'react';
import { generatePath, useParams, useRouteMatch } from 'react-router-dom';
import {
  ActionButton,
  Panel,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleButtonsGroup,
  PanelTitleWrapper
} from 'components/panel';
import { Breadcrumb, Breadcrumbs } from 'components/breadcrumbs';
import { TabList, TabPanel, TabPanelBody, Tabs } from 'components/tabs';
import Badge from 'components/badge/Badge';
import InjuriesDetailsTab from 'containers/pages/rehab/injury/InjuriesDetailsTab';
import InjuriesScreeningsTab from 'containers/pages/rehab/injury/InjuriesScreeningsTab';
import { useMedicalContext } from 'contexts/MedicalContext';
import { FormattedMessage } from 'react-intl';
import messages from 'messages';
import {
  ROUTE_PERSONS_ENTITY,
  ROUTE_PERSONS_ENTITY_PERSON,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_REPORTS,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_ENTITY,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_REPORT_TESTDATA,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS_TESTDATA,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS_TESTDATA_EDIT,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_RETEST,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_DETAIL,
  ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_DETAIL_EDIT
} from 'routes/RouteList';
import useTabIndex from 'hooks/utils/useTabIndex';
import { getLocalDateFormat } from 'utils/date';
import { sort, SORT_DATA_TYPES } from 'utils/sort';
import PersonInjury from 'models/PersonInjury';
import TabLink from 'components/tabs/TabLink';
import { useEntityContext } from 'containers/pages/persons/store/useEntityContext';
import usePerson from 'hooks/queries/usePerson';
import ContextMenuWrapper from 'components/menu/ContextMenuWrapper';
import ContextMenu from 'components/menu/ContextMenu';

function Injury() {
  const { entityId, groupId, personId, medicalId, injuryId } = useParams();
  const { fetchPerson } = usePerson({ personId: personId });
  const person = useEntityContext(s => s.person);
  const { medical } = useMedicalContext();
  const [personInjury, setPersonInjury] = useState(new PersonInjury({}));
  const [sides, setSides] = useState({});
  const edit = useRouteMatch(
    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL_EDIT
  )?.isExact;

  const [tabIndex, setTabIndex] = useTabIndex([
    [
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL_EDIT
    ],
    [
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_ENTITY,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_DETAIL,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_DETAIL_EDIT,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS_TESTDATA,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_TESTS_TESTDATA_EDIT,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_RETEST,
      ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS_REPORT_TESTDATA
    ],
    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_REPORTS
  ]);

  const active = useRouteMatch([
    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS,
    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL_EDIT,
    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL
  ]);

  useEffect(() => {
    const fetchPersonData = async () => {
      await fetchPerson(personId);
    };
    if (!person.id) {
      fetchPersonData();
    }
  }, [entityId, personId]);

  useEffect(() => {
    if (medical?.injuries) {
      const personInjury = medical.injuries.find(i => i.id === injuryId);

      if (personInjury) {
        setPersonInjury(personInjury);
      }

      if (personInjury?.screenings) {
        const sortedScreenings = sort(personInjury.screenings, {
          keys: [{ key: 'startdate', dataType: SORT_DATA_TYPES.DATE_STRING }]
        });
        setPersonInjury(
          new PersonInjury({ ...personInjury, screenings: sortedScreenings })
        );
      }

      if (personInjury?.sides) {
        setSides({
          left:
            (personInjury.sides.hasOwnProperty('left') &&
              personInjury.sides.left) ||
            false,
          right:
            (personInjury.sides.hasOwnProperty('right') &&
              personInjury.sides.right) ||
            false
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medical, injuryId]);

  return (
    <Panel active={active.isExact}>
      {person && personInjury?.injury && (
        <>
          <PanelHeader>
            <Breadcrumbs>
              <Breadcrumb
                to={generatePath(ROUTE_PERSONS_ENTITY, { entityId, groupId })}
              >
                <FormattedMessage {...messages.breadcrumbSporters} />
              </Breadcrumb>
              <Breadcrumb
                to={generatePath(ROUTE_PERSONS_ENTITY_PERSON, {
                  entityId,
                  groupId,
                  personId
                })}
              >
                {`${person.firstname} ${person.lastname}`}
              </Breadcrumb>
            </Breadcrumbs>
            <PanelTitleWrapper>
              <PanelTitle>
                {personInjury.injury.title}
                {sides &&
                  Object.entries(sides).length > 0 &&
                  Object.entries(sides).some(e => e[1]) && (
                    <Badge
                      lighterFont
                      extraClassNames="u-margin-left-small u-margin-right-auto"
                    >
                      <FormattedMessage
                        {...messages.leftRightBadge}
                        values={{
                          sides: Object.entries(sides).every(e => e[1])
                            ? 'both'
                            : Object.entries(sides).find(e => e[1])[0]
                        }}
                      />
                    </Badge>
                  )}
              </PanelTitle>
              <PanelSubtitle>
                {getLocalDateFormat(personInjury.injuryDate)}
                {personInjury.injuryEndDate && (
                  <>
                    {' - '}
                    {getLocalDateFormat(personInjury.injuryEndDate)}
                  </>
                )}
              </PanelSubtitle>
            </PanelTitleWrapper>
            <PanelTitleButtonsGroup>
              <ActionButton />
              <ContextMenuWrapper trigger={<PanelTitleButton />}>
                <ContextMenu />
              </ContextMenuWrapper>
            </PanelTitleButtonsGroup>
          </PanelHeader>
          <PanelBody>
            <Tabs
              selectedIndex={tabIndex}
              onSelect={index => setTabIndex(index)}
            >
              <TabList>
                <TabLink
                  to={generatePath(
                    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL,
                    {
                      entityId,
                      groupId,
                      personId,
                      medicalId,
                      injuryId
                    }
                  )}
                  key="injuries.detail"
                >
                  <FormattedMessage {...messages.rehabInjuryDetailsTab} />
                </TabLink>
                <TabLink
                  key="injuries.screenings"
                  to={generatePath(
                    ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_SCREENINGS,
                    {
                      entityId,
                      groupId,
                      personId,
                      medicalId,
                      injuryId
                    }
                  )}
                >
                  {personInjury?.screenings && (
                    <Badge>{personInjury.screenings.length}</Badge>
                  )}
                  <FormattedMessage {...messages.rehabInjuryScreeningsTab} />
                </TabLink>
              </TabList>
              <TabPanel key="injuries.detail">
                <TabPanelBody withScroller>
                  <InjuriesDetailsTab
                    personInjury={personInjury}
                    edit={edit}
                    medical={medical}
                  />
                </TabPanelBody>
              </TabPanel>
              <TabPanel key="injuries.screenings">
                <TabPanelBody withScroller>
                  <InjuriesScreeningsTab
                    entityId={entityId}
                    groupId={groupId}
                    personInjury={personInjury}
                    medical={medical}
                  />
                </TabPanelBody>
              </TabPanel>
            </Tabs>
          </PanelBody>
        </>
      )}
    </Panel>
  );
}

export default Injury;
